import { useId } from 'react';

/**
 * A labelled form control.
 *
 * Every input gets a real `<label for>`, an accessible description when a hint
 * is present, and `aria-invalid` plus `aria-describedby` wired to the error —
 * so a screen reader announces the problem rather than only showing red.
 */
export function Field({
  label,
  type = 'text',
  value,
  onChange,
  error,
  hint,
  required,
  autoComplete,
  disabled,
  placeholder,
  spellCheck,
}: {
  label: string;
  type?: 'text' | 'email' | 'password';
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Off for values that are not prose — a pasted URL or key, say. */
  spellCheck?: boolean;
}): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((entry): entry is string => entry !== null)
    .join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required === true ? (
          <span aria-hidden="true" style={{ color: 'var(--c-text-muted)' }}>
            {' '}
            *
          </span>
        ) : null}
      </label>

      {hint === undefined ? null : (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}

      <input
        className="field__input"
        id={id}
        type={type}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        required={required}
        autoComplete={autoComplete}
        disabled={disabled}
        placeholder={placeholder}
        spellCheck={spellCheck}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy === '' ? undefined : describedBy}
      />

      {error === undefined ? null : (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
}
